import { supabase } from './supabase';

const BASE_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;

// Helper function to make authenticated API calls to edge functions
export const callEdgeFunction = async (
  endpoint: string, 
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET',
  data?: any
) => {
  // Get the current session
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session) {
    throw new Error('No authenticated session found. Please log in.');
  }
  
  const url = `${BASE_URL}/${endpoint}`;
  
  const options: RequestInit = {
    method,
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
  };
  
  if (data && (method === 'POST' || method === 'PUT')) {
    options.body = JSON.stringify(data);
  }
  
  const response = await fetch(url, options);
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'An unknown error occurred' }));
    throw new Error(error.error || `API request failed with status ${response.status}`);
  }
  
  return response.json();
};

// User management API functions
export const userApi = {
  // List all users (with pagination)
  listUsers: (page = 1, pageSize = 50) => {
    return callEdgeFunction(`user-management/users?page=${page}&pageSize=${pageSize}`);
  },
  
  // Invite a new user
  inviteUser: (email: string, role: string = 'authenticated') => {
    return callEdgeFunction('user-management/users/invite', 'POST', { email, role });
  },
  
  // Update a user's role
  updateUserRole: (userId: string, role: string) => {
    return callEdgeFunction(`user-management/users/${userId}/role`, 'PUT', { role });
  },
  
  // Ban or unban a user
  updateUserBanStatus: (userId: string, isBanned: boolean) => {
    return callEdgeFunction(`user-management/users/${userId}/ban`, 'PUT', { isBanned });
  },
  
  // Delete a user
  deleteUser: (userId: string) => {
    return callEdgeFunction(`user-management/users/${userId}`, 'DELETE');
  },
};
